/**
 * Bepaalt welke eigen (custom) matrix-aantallen weer verwijderd mogen worden
 * omdat een voltooide prijsaanvraag ze echt niet kon prijzen - nooit omdat
 * ze simpelweg nog niet opgehaald zijn.
 *
 * Gebruikt door de "auto-remove custom quantities"-effect in
 * product-configurator.tsx. PRINTCOM en CUSTOM hebben elk hun eigen "is dit
 * aantal geprijsd?"-bron, en die twee mogen niet door elkaar gehaald worden:
 *
 * - CUSTOM: de rijen in de prijstabel komen per aantal van de server
 *   (/api/pricing/calculate, zie het CUSTOM-blok bij deliveryQuantityRows).
 *   Vroeger keek deze functie naar quantityPrices, maar dit effect draait in
 *   dezelfde commit als het toevoegen, vóórdat de prijseffecten
 *   isPriceLoading op true zetten. Een net ingetypt aantal (bijv. 7 stuks
 *   reclamebord) stond dan nog niet in quantityPrices en werd meteen weer
 *   verwijderd: het veld leegde zich en er verscheen geen rij. Daarom geldt
 *   voor CUSTOM dezelfde regel als voor PRINTCOM: alleen verwijderen als de
 *   server de rij al heeft geprobeerd en geen enkele cel kon prijzen.
 * - PRINTCOM: de matrixaanvraag (het gedebouncede batch-effect) schrijft
 *   alleen quantityPrices[geselecteerdAantal] - de prijs van het op dit
 *   moment gekozen aantal, voor de totaalprijs in de zijbalk. Het is geen
 *   prijs-per-rij-kaart. Een net toegevoegd eigen aantal heeft daar dus
 *   vrijwel altijd quantityPrices[aantal] === undefined, ook al is de
 *   gedebouncede matrixaanvraag (700ms) nog niet eens begonnen - waardoor
 *   het aantal er meteen weer uit werd gehaald voordat de prijs ooit kon
 *   laden. Voor PRINTCOM moet daarom tegen deliveryQuantityRows gecheckt
 *   worden: alleen verwijderen als er al een rij voor dat aantal bestaat
 *   (een voltooide aanvraag heeft 'm dus al geprobeerd) én geen enkele
 *   kolomcel beschikbaar is. Bestaat er nog geen rij, dan heeft de
 *   gedebouncede aanvraag gewoon nog geen kans gehad - niet verwijderen.
 */

export type DeliveryPriceCellLike = { available?: boolean } | undefined;
export type DeliveryQuantityRowLike = { quantity: number; cells: Record<string, DeliveryPriceCellLike> };

export function computeCustomQuantitiesToRemove(input: {
  providerType: string | undefined;
  customMatrixQuantities: number[];
  quantityPrices: Record<number, number>;
  deliveryQuantityRows: DeliveryQuantityRowLike[];
}): number[] {
  const { providerType, customMatrixQuantities, quantityPrices, deliveryQuantityRows } = input;

  if (providerType === 'PRINTCOM' || providerType === 'CUSTOM') {
    return customMatrixQuantities.filter((quantity) => {
      const row = deliveryQuantityRows.find((candidate) => candidate.quantity === quantity);
      if (!row) return false;
      return !Object.values(row.cells).some((cell) => cell?.available);
    });
  }

  return [];
}
